import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IClientPortalAccountsService } from "services/ClientPortalAccountsService";
import {
  ClientDDFrequencies,
  ClientPortalPaymentModel,
  ClientPortalProposedWorkDetailsModel,
  ClientPropertyBalanceListModel,
  CreatePaymentResponseModel,
  FundScheduleChargeCategories,
  MonthlyDirectDebitCalculationModel,
  QuarterlyDirectDebitCalculationModel,
  PaymentRequestModel,
} from "services/api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { RootState } from "setup/redux/RootReducer";
import { PayNowFormSteps, PaymentAmount } from "./steps/StepModel";
import { IClientPortalPaymentsService } from "services/ClientPortalPaymentsService";
import { IClientPortalProposedWorksService } from "services/ClientPortalProposedWorksService";
import { toast } from "react-toastify";

type ClientDirectDebitAuthorisationURL = {
  [clientID: string]: string;
};

type PayNowPageState = {
  Loading: boolean;
  CurrentStep: PayNowFormSteps;
  Balances: ClientPropertyBalanceListModel;
  ProposedWorkDetails: ClientPortalProposedWorkDetailsModel;
  CreatePaymentResponse: CreatePaymentResponseModel;
  PaymentConfirmationEmail: string | null;
  Status: string;
  PaymentAmounts: Array<PaymentAmount>;
  BlinkACSForm: string;
  ClientPortalPayment: ClientPortalPaymentModel | null;
  AllowedDirectDebitCollectionDays: Array<number>;
  DirectDebitDetailsLoading: boolean;
  MonthlyDirectDebitDetails: MonthlyDirectDebitCalculationModel | null;
  QuarterlyDirectDebitDetails: QuarterlyDirectDebitCalculationModel | null;
  DirectDebitAuthorisationURL: ClientDirectDebitAuthorisationURL | null;
};

const initialState: PayNowPageState = {
  Loading: false,
  CurrentStep: PayNowFormSteps.PaymentBreakdown,
  Balances: new ClientPropertyBalanceListModel(),
  ProposedWorkDetails: new ClientPortalProposedWorkDetailsModel(),
  CreatePaymentResponse: new CreatePaymentResponseModel(),
  PaymentConfirmationEmail: null,
  Status: "",
  PaymentAmounts: new Array<PaymentAmount>(),
  BlinkACSForm: "",
  ClientPortalPayment: null,
  AllowedDirectDebitCollectionDays: new Array<number>(),
  DirectDebitDetailsLoading: false,
  MonthlyDirectDebitDetails: null,
  QuarterlyDirectDebitDetails: null,
  DirectDebitAuthorisationURL: null,
};

const handleSetCurrentStep = (state, action) => {
  state.CurrentStep = action.payload;
};

export const PaymentsSlice = createSlice({
  name: "Payments",
  initialState,
  reducers: {
    setCurrentStep: (state, action) => handleSetCurrentStep(state, action),
    incrementCurrentStep: (state) => {
      state.CurrentStep += 1;
      state.Status = "";
    },
    decrementCurrentStep: (state) => {
      if (state.CurrentStep > 0) {
        state.CurrentStep -= 1;
        state.Status = "";
      }
    },
    setStatus: (state, action) => {
      state.Status = action.payload;
    },
    setPaymentConfirmationEmail: (state, action) => {
      state.PaymentConfirmationEmail = action.payload;
    },
    resetState: (state) => {
      state.Loading = initialState.Loading;
      state.CurrentStep = initialState.CurrentStep;
      state.Balances = initialState.Balances;
      state.CreatePaymentResponse = initialState.CreatePaymentResponse;
      state.Status = initialState.Status;
    },
    setPaymentAmounts: (state, action) => {
      state.PaymentAmounts = action.payload;
    },
    clearBlinkACSForm: (state) => {
      state.BlinkACSForm = initialState.BlinkACSForm;
    },
    resetDirectDebitAuthorisationURL: (state) => {
      state.DirectDebitAuthorisationURL = null;
    },
    setLoading: (state, action) => {
      state.Loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBalancesForAllAccounts.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getBalancesForAllAccounts.fulfilled, (state, action) => {
        const currentBalances = state.Balances.propertyBalances;
        const newBalances = action.payload.propertyBalances;
        if (
          currentBalances !== undefined &&
          newBalances !== undefined &&
          currentBalances.some((x, i) => {
            return x.clientDirectDebitDetails == undefined && newBalances[i].clientDirectDebitDetails != undefined;
          })
        ) {
          toast.success("Direct debit set up successfully");
        }

        state.Balances = action.payload;
        state.Loading = false;
      })
      .addCase(getBalancesForAllAccounts.rejected, (state) => {
        state.Balances = new ClientPropertyBalanceListModel();
        state.Loading = false;
      })
      .addCase(createPaymentRequest.pending, (state) => {
        state.Loading = true;
      })
      .addCase(createPaymentRequest.fulfilled, (state, action) => {
        state.CreatePaymentResponse = action.payload;
        state.CurrentStep += 1;
        state.Loading = false;
      })
      .addCase(createPaymentRequest.rejected, (state) => {
        state.CreatePaymentResponse = new CreatePaymentResponseModel();
        state.Loading = false;
      })
      .addCase(processBlinkPayment.pending, (state) => {
        state.Loading = true;
      })
      .addCase(processBlinkPayment.fulfilled, (state, action) => {
        state.BlinkACSForm = action.payload.acsform!;
        state.Loading = false;
      })
      .addCase(processBlinkPayment.rejected, (state) => {
        handleSetCurrentStep(state, { payload: PayNowFormSteps.PaymentFailed, type: "" });
        state.Loading = false;
      })
      .addCase(getClientPortalPayment.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getClientPortalPayment.fulfilled, (state, action) => {
        state.ClientPortalPayment = action.payload;
        state.Loading = false;
      })
      .addCase(getClientPortalPayment.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(setPaymentConfirmationEmail.pending, (state) => {
        state.Loading = true;
      })
      .addCase(setPaymentConfirmationEmail.fulfilled, (state, action) => {
        state.PaymentConfirmationEmail = action.payload;
        state.Loading = false;
      })
      .addCase(setPaymentConfirmationEmail.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(getProposedWorkDetails.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getProposedWorkDetails.fulfilled, (state, action) => {
        state.ProposedWorkDetails = action.payload;
        state.Loading = false;
      })
      .addCase(getProposedWorkDetails.rejected, (state) => {
        state.ProposedWorkDetails = new ClientPortalProposedWorkDetailsModel();
        state.Loading = false;
      })
      .addCase(getAllowedDirectDebitCollectionDays.pending, (state) => {
        state.DirectDebitDetailsLoading = true;
      })
      .addCase(getAllowedDirectDebitCollectionDays.fulfilled, (state, action) => {
        state.DirectDebitDetailsLoading = false;
        state.AllowedDirectDebitCollectionDays = action.payload;
      })
      .addCase(getAllowedDirectDebitCollectionDays.rejected, (state) => {
        state.DirectDebitDetailsLoading = false;
      })
      .addCase(calculateMonthlyDirectDebitDetails.pending, (state) => {
        state.QuarterlyDirectDebitDetails = null;
        state.DirectDebitDetailsLoading = true;
      })
      .addCase(calculateMonthlyDirectDebitDetails.fulfilled, (state, action) => {
        state.DirectDebitDetailsLoading = false;
        state.MonthlyDirectDebitDetails = action.payload;
      })
      .addCase(calculateMonthlyDirectDebitDetails.rejected, (state) => {
        state.DirectDebitDetailsLoading = false;
      })
      .addCase(calculateQuarterlyDirectDebitDetails.pending, (state) => {
        state.MonthlyDirectDebitDetails = null;
        state.DirectDebitDetailsLoading = true;
      })
      .addCase(calculateQuarterlyDirectDebitDetails.fulfilled, (state, action) => {
        state.DirectDebitDetailsLoading = false;
        state.QuarterlyDirectDebitDetails = action.payload;
      })
      .addCase(calculateQuarterlyDirectDebitDetails.rejected, (state) => {
        state.DirectDebitDetailsLoading = false;
      })
      .addCase(setUpDirectDebit.pending, (state) => {
        state.DirectDebitDetailsLoading = true;
      })
      .addCase(setUpDirectDebit.fulfilled, (state, action) => {
        state.DirectDebitDetailsLoading = false;
        state.DirectDebitAuthorisationURL = action.payload;
      })
      .addCase(setUpDirectDebit.rejected, (state) => {
        state.DirectDebitDetailsLoading = false;
      })
      .addCase(cancelDirectDebit.pending, (state) => {
        state.Loading = true;
      })
      .addCase(cancelDirectDebit.fulfilled, (state) => {
        toast.success("Direct debit cancelled");
        state.Loading = false;
      })
      .addCase(cancelDirectDebit.rejected, (state) => {
        state.Loading = false;
      });
  },
});

export const getProposedWorkDetails = createAsyncThunk<
  ClientPortalProposedWorkDetailsModel,
  { proposedWorkID: string }
>("PayNow/GetProposedWorksDetails", async ({ proposedWorkID }) => {
  const proposedWorksService = container.get<IClientPortalProposedWorksService>(
    SERVICE_TYPES.ClientPortalProposedWorksService
  );
  return await proposedWorksService.getProposedWorksDetail(proposedWorkID);
});

export const getBalancesForAllAccounts = createAsyncThunk("PayNow/GetBalancesForAllAccounts", async () => {
  const clientPortalAccountsService = container.get<IClientPortalAccountsService>(
    SERVICE_TYPES.ClientPortalAccountsService
  );
  return await clientPortalAccountsService.getBalancesForAllProperties();
});

export const setPaymentConfirmationEmail = createAsyncThunk(
  "PayNow/SetPaymentConfirmationEmail",
  async (data: { email: string; clientPortalPaymentID: string }) => {
    const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
      SERVICE_TYPES.ClientPortalPaymentsService
    );
    await clientPortalPaymentsService.setPaymentConfirmationEmail(data.email, data.clientPortalPaymentID);
    return data.email;
  }
);

export const createPaymentRequest = createAsyncThunk(
  "PayNow/CreatePaymentRequest",
  async (paymentRequestModel: PaymentRequestModel) => {
    const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
      SERVICE_TYPES.ClientPortalPaymentsService
    );
    return await clientPortalPaymentsService.createPaymentRequest(paymentRequestModel);
  }
);

export const processBlinkPayment = createAsyncThunk(
  "PayNow/ProcessBlinkPayment",
  async (paymentDetailsJson: string) => {
    const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
      SERVICE_TYPES.ClientPortalPaymentsService
    );
    return await clientPortalPaymentsService.processBlinkPayment(paymentDetailsJson);
  }
);

export const getClientPortalPayment = createAsyncThunk(
  "PayNow/GetClientPortalPayment",
  async (clientPortalPaymentID: string | undefined) => {
    const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
      SERVICE_TYPES.ClientPortalPaymentsService
    );
    return await clientPortalPaymentsService.getClientPortalPayment(clientPortalPaymentID);
  }
);

export const getAllowedDirectDebitCollectionDays = createAsyncThunk(
  "PayNow/GetAllowedDirectDebitCollectionDays",
  async () => {
    const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
      SERVICE_TYPES.ClientPortalPaymentsService
    );
    return await clientPortalPaymentsService.getAllowedDirectDebitCollectionDays();
  }
);

export const calculateMonthlyDirectDebitDetails = createAsyncThunk<
  MonthlyDirectDebitCalculationModel,
  { clientID: string; collectionDay: number; fundScheduleChargeCategoryID?: FundScheduleChargeCategories }
>("PayNow/CalculateMonthlyDirectDebitDetails", async ({ clientID, collectionDay, fundScheduleChargeCategoryID }) => {
  const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
    SERVICE_TYPES.ClientPortalPaymentsService
  );
  return await clientPortalPaymentsService.calculateMonthlyDirectDebitDetails(
    clientID,
    collectionDay,
    fundScheduleChargeCategoryID
  );
});

export const calculateQuarterlyDirectDebitDetails = createAsyncThunk<
  QuarterlyDirectDebitCalculationModel,
  { clientID: string; collectionDay: number; fundScheduleChargeCategoryID?: FundScheduleChargeCategories }
>("PayNow/CalculateQuarterlyDirectDebitDetails", async ({ clientID, collectionDay, fundScheduleChargeCategoryID }) => {
  const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
    SERVICE_TYPES.ClientPortalPaymentsService
  );
  return await clientPortalPaymentsService.calculateQuarterlyDirectDebitDetails(
    clientID,
    collectionDay,
    fundScheduleChargeCategoryID
  );
});

export const setUpDirectDebit = createAsyncThunk<
  ClientDirectDebitAuthorisationURL,
  {
    clientID: string;
    collectionDay: number;
    frequency: ClientDDFrequencies;
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories;
  }
>("PayNow/SetUpDirectDebit", async ({ clientID, collectionDay, frequency, fundScheduleChargeCategoryID }) => {
  const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
    SERVICE_TYPES.ClientPortalPaymentsService
  );

  const key = `${clientID}_${fundScheduleChargeCategoryID ?? ""}`;
  return {
    [key]:
      (
        await clientPortalPaymentsService.setUpDirectDebit(
          clientID,
          collectionDay,
          frequency,
          fundScheduleChargeCategoryID
        )
      ).authorisationURL ?? "",
  };
});

export const cancelDirectDebit = createAsyncThunk(
  "PayNow/CancelDirectDebit",
  async ({
    clientID,
    fundScheduleChargeCategoryID,
  }: {
    clientID: string;
    fundScheduleChargeCategoryID?: FundScheduleChargeCategories;
  }) => {
    const clientPortalPaymentsService = container.get<IClientPortalPaymentsService>(
      SERVICE_TYPES.ClientPortalPaymentsService
    );
    await clientPortalPaymentsService.cancelDirectDebit(clientID, fundScheduleChargeCategoryID);
  }
);

export const {
  setCurrentStep,
  incrementCurrentStep,
  decrementCurrentStep,
  setStatus,
  resetState,
  setPaymentAmounts,
  clearBlinkACSForm,
  resetDirectDebitAuthorisationURL,
  setLoading,
} = PaymentsSlice.actions;

export const selectBalances = (state: RootState) => state.payNow.Balances;
export const selectProposedWorkDetails = (state: RootState) => state.payNow.ProposedWorkDetails;
export const selectLoading = (state: RootState) => state.payNow.Loading;
export const selectCurrentStep = (state: RootState) => state.payNow.CurrentStep;
export const selectCreatePaymentResponse = (state: RootState) => state.payNow.CreatePaymentResponse;
export const selectPaymentConfirmationEmail = (state: RootState) => state.payNow.PaymentConfirmationEmail;
export const selectPaymentAmounts = (state: RootState) => state.payNow.PaymentAmounts; // do we need this?
export const selectBlinkACSForm = (state: RootState) => state.payNow.BlinkACSForm;
export const selectClientPortalPayment = (state: RootState) => state.payNow.ClientPortalPayment;
export const selectDirectDebitDetailsLoading = (state: RootState) => state.payNow.DirectDebitDetailsLoading;
export const selectAllowedDirectDebitCollectionDays = (state: RootState) =>
  state.payNow.AllowedDirectDebitCollectionDays;
export const selectMonthlyDirectDebitDetails = (state: RootState) => state.payNow.MonthlyDirectDebitDetails;
export const selectQuarterlyDirectDebitDetails = (state: RootState) => state.payNow.QuarterlyDirectDebitDetails;
export const selectDirectDebitAuthorisationURL = (state: RootState) => state.payNow.DirectDebitAuthorisationURL;

export const isDirectDebitCollectionAvailable = (
  frequency: ClientDDFrequencies | undefined,
  monthlyDetails: MonthlyDirectDebitCalculationModel | null,
  quarterlyDetails: QuarterlyDirectDebitCalculationModel | null
): boolean =>
  frequency === ClientDDFrequencies.Monthly
    ? (monthlyDetails?.monthlyCollectionAmount ?? 0) > 0
    : quarterlyDetails?.variable
    ? true
    : (quarterlyDetails?.quarterlyCollectionAmount ?? 0) > 0;

export default PaymentsSlice.reducer;
